import { useQuery } from '@tanstack/react-query';
import {
  addDays,
  type BankMovementResponse,
  formatMoney,
  fromCents,
  toCents,
  type TransferChoice,
  type TransferOptions,
  type TransferReason,
  TRANSFER_DAYS,
} from '@k-management/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { movementPath } from './treasury-ui.js';

/** «nessuno»: not there yet; «crea»: the cash movement made with it. */
type Pick = string | 'nessuno' | 'crea';

/**
 * The other side of a transfer or a withdrawal (3.13): the other account, the
 * movement with the opposite amount within four days on it, or none yet (a
 * statement still to load), or a cash movement to create. A withdrawal goes
 * between a bank and the cash.
 */
export function TransferPicker({
  movement,
  reason,
  onChange,
}: {
  movement: BankMovementResponse;
  reason: TransferReason;
  /** Null while the choice is not complete. */
  onChange: (choice: TransferChoice | null) => void;
}) {
  const options = useQuery({
    queryKey: ['treasury', 'transfers', 'options', movement.id, reason],
    queryFn: () =>
      apiRequest<TransferOptions>(`/treasury/transfers/options/${movement.id}?reason=${reason}`),
  });
  const [accountCode, setAccountCode] = useState('');
  const [pick, setPick] = useState<Pick | null>(null);
  const data = options.data;

  // The closest movement comes chosen; without one, the cash movement to create.
  const firstPick = (code: string): Pick | null => {
    const first = data?.candidates.find((item) => item.accountCode === code);
    if (first) return first.id;
    return data?.accounts.find((item) => item.code === code)?.kind === 'Cassa' ? 'crea' : null;
  };
  useEffect(() => {
    if (!data) return;
    const account = data.suggestedAccount ?? '';
    setAccountCode(account);
    setPick(firstPick(account));
  }, [data]);

  const account = data?.accounts.find((item) => item.code === accountCode);
  const shown = (data?.candidates ?? []).filter((item) => item.accountCode === accountCode);
  const isCash = account?.kind === 'Cassa';

  useEffect(() => {
    if (!accountCode || pick === null) onChange(null);
    else if (pick === 'nessuno') onChange({ accountCode, movementId: null });
    else if (pick === 'crea') onChange({ accountCode, movementId: null, create: true });
    else onChange({ accountCode, movementId: pick });
  }, [accountCode, pick, onChange]);

  if (options.isLoading) return <p className="muted">Caricamento…</p>;
  if (options.error) return <p className="error">{options.error.message}</p>;
  if (!data) return null;
  const incoming = Number(movement.amount) > 0;
  return (
    <div className="stack">
      <Field
        label="Altro conto"
        hint={
          reason === 'prelievo'
            ? 'Un prelievo va sempre fra una banca e la Cassa.'
            : 'Le banche e la Cassa; il giroconto si sistema con il movimento dell’altro conto.'
        }
      >
        <select
          value={accountCode}
          onChange={(event) => {
            const code = event.target.value;
            setAccountCode(code);
            setPick(firstPick(code));
          }}
        >
          <option value="">— scegli —</option>
          {data.accounts.map((item) => (
            <option key={item.code} value={item.code}>
              {item.alias}
            </option>
          ))}
        </select>
      </Field>
      {account && (
        <div className="stack">
          <small className="muted">
            Movimenti di {account.alias} di {formatMoney(fromCents(-toCents(movement.amount)))} (
            {incoming ? 'in uscita' : 'in entrata'}), dal{' '}
            {formatDate(addDays(movement.bookingDate, -TRANSFER_DAYS))} al{' '}
            {formatDate(addDays(movement.bookingDate, TRANSFER_DAYS))}:
          </small>
          {shown.map((item) => (
            <label key={item.id} className="checkbox">
              <input
                type="radio"
                name="transfer"
                checked={pick === item.id}
                onChange={() => setPick(item.id)}
              />{' '}
              <strong>{formatDate(item.bookingDate)}</strong> · {formatMoney(item.amount)} ·{' '}
              {item.description}{' '}
              <span className="pill info">
                {item.days === 0
                  ? 'stesso giorno'
                  : `${item.days} ${item.days === 1 ? 'giorno' : 'giorni'}`}
              </span>
              {item.state === 'giroconto' && (
                <span className="pill">già segnato come giroconto</span>
              )}{' '}
              <Link to={movementPath(item.accountCode, item.id)} target="_blank">
                apri
              </Link>
            </label>
          ))}
          {isCash && (
            <label className="checkbox">
              <input
                type="radio"
                name="transfer"
                checked={pick === 'crea'}
                onChange={() => setPick('crea')}
              />{' '}
              Crea il movimento in {account.alias} ({formatDate(movement.bookingDate)},{' '}
              {formatMoney(fromCents(-toCents(movement.amount)))})
            </label>
          )}
          <label className="checkbox">
            <input
              type="radio"
              name="transfer"
              checked={pick === 'nessuno'}
              onChange={() => setPick('nessuno')}
            />{' '}
            Non c’è ancora: resta da abbinare in Da sistemare
          </label>
        </div>
      )}
    </div>
  );
}
